/*
 * @Author: Ryan
 * @Date: 2026-03-11
 * @Description: Features section - Scale Your Influence with AI Twin Creation (alternating layout)
 */
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

interface FeatureItem {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

const features: FeatureItem[] = [
  {
    title: 'Transform Any Portraits into Talking Twins',
    description:
      "Convert any high-resolution profile picture into a vivid, talking personality in moments. FalcoCut leverages next-generation motion-tracking to animate static captures with realistic gestures and authentic mouth movements. It is the ultimate shortcut for crafting polished introductions or rapid social clips when you don't have time for a full shoot. Just provide the image, and our engine handles the rest, delivering a lifelike performance every time.",
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/prompts-to-video.webp',
    imageAlt: 'Transform portraits into talking AI twins',
  },
  {
    title: 'Seamless Voice Cloning & Lip-Sync',
    description:
      'Achieve total vocal authenticity by cloning your own voice for every video. Our state-of-the-art AI maps the subtle nuances of your pitch and pacing, ensuring your AI replica sounds indistinguishable from a live recording. The system automatically aligns every syllable with precision lip-syncing, eliminating the "uncanny valley" effect. Now, your video maintains your signature voice style across every frame, providing a personalized touch that builds deep trust with your audience.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/trend-style-clips.webp',
    imageAlt: 'Seamless voice cloning and lip-sync technology',
  },
  {
    title: 'Break Borders with Global Localization',
    description:
      'Expand your reach instantly with support for over 30 languages and regional accents. Choose from our vast library of preset AI voices to give your digital twin a localized flair that resonates with international markets. Whether you need a formal tone for an explainer or a casual vibe for a vlog, our multi-dialect support ensures your message is never lost in translation. Scale your brand globally without the need for a multilingual production team.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/product-to-video.webp',
    imageAlt: 'Global localization with 30+ languages',
  },
  {
    title: 'Smart Captions for Maximum Engagement',
    description:
      "Boost your video accessibility and retention with automatically generated on-screen captions. FalcoCut transcribes your audio in real-time, overlaying high-contrast text that keep viewers engaged even when their sound is off. You can select various subtitle styles to match your brand's aesthetic perfectly. These dynamic overlays are vital for capturing attention on fast-paced platforms, ensuring your message lands even when viewers are scrolling on mute.",
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/voiceovers-captions.webp',
    imageAlt: 'Smart captions for maximum video engagement',
  },
  {
    title: 'Stay Consistent Across Every Video',
    description:
      'Maintaining a consistent posting schedule is the hardest part of being a creator, but a digital twin makes it effortless. By removing the need for hair, makeup, lighting, and set prep, FalcoCut reduces your production time from hours to minutes. This is the most cost-effective way to ensure your face stays in front of your community every single day. Keep your brand identity strong and recognizable across all platforms without the looming threat of creator burnout.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/long-to-shorts.webp',
    imageAlt: 'Consistent brand presence across every video',
  },
  {
    title: 'Multiply Social Presence with Virtual You',
    description:
      'Construct a month\'s worth of viral-ready content in a single afternoon without ever re-recording a scene. FalcoCut empowers you to churn out an array of influencer-style "talking head" clips specifically tailored for the fast-paced algorithms of Reels, TikTok, and YouTube Shorts. You can swap backgrounds and scripts instantly, creating endless variety from just one initial setup. By flooding your channels with high-quality interactions, you effectively capture the attention of new audiences across every major social platform.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/publish-ready.webp',
    imageAlt: 'Multiply social presence with virtual you',
  },
];

const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

interface FeatureCardProps {
  feature: FeatureItem;
  isReversed: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ feature, isReversed }) => {
  return (
    <div
      className={`flex flex-col ${isReversed ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-8 pad:gap-10 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden bg-gray-100">
          <Image
            src={feature.imageUrl}
            alt={feature.imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full">
        <h3 className="text-xl pad:text-2xl font-bold text-gray-900 mb-4">
          {feature.title}
        </h3>
        <p className="text-sm pad:text-base text-gray-600 leading-relaxed mb-6">
          {feature.description}
        </p>
        <Link
          href="/ai-video-generator"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
          style={{ background: '#745CFF' }}
        >
          <span>Create Your AI Twin</span>
          <ArrowRightIcon />
        </Link>
      </div>
    </div>
  );
};

const FeaturesSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Header */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
          Scale Your Influence with AI Twin Creation
        </h2>
        <p className="text-sm pad:text-base text-gray-600 text-center max-w-4xl mx-auto mb-12 pad:mb-16">
          Bring your brand to life using FalcoCut to generate a hyper-realistic digital
          twin that speaks your language. Our platform automates the entire production
          workflow, allowing you to maintain a consistent on-camera presence without ever
          touching a camera.
        </p>

        {/* Features List */}
        <div className="space-y-16 pad:space-y-20">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              isReversed={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
